import React, { useEffect, useMemo, useState } from 'react';
import { getLinearGraphData } from '../../data/analytics/chartjsData'
import SummaryGraphs from './SummaryGraphs'
import SummaryCard from './SummaryCard';
import { getTimeInSecs } from '../../services/utils/analyticsUtils';
import dayjs from 'dayjs';
import SummaryGrid from './SummaryGrid';
import LoadingCard from '../Components/Cards/LoadingCard';

const overviewLabels = [
    'totalUser',
    'totalSession',
    'totalNewUser',
    'totalAvgTime',
    'totalAvgEndTime',
    'totalAvgSpU',
];

// Funzione per convertire i secondi in minuti con decimali
const convertSecondsToMinutes = (seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

function AnalyticsOverviewCard({ getData, loading, aggregateList = [] }) {
    const [userData, setUserData] = useState({});
    const [newUserData, setNewUserData] = useState({});
    const [sessionData, setSessionData] = useState({});
    const [avgTimeData, setAvgTimeData] = useState({});
    const [avgEngTimeData, setAvgEngTimeData] = useState({});
    const [avgSessionPerUserData, setAvgSessionPerUserData] = useState({});
    const [selectedDataIndex, setSelectedDataIndex] = useState(0);

    const handleDataIndexChange = (index) => {
        setSelectedDataIndex(index);
    };

    const sortArrByDate = ({ data, labels }) => {
        const dataArray = labels.map((label, index) => ({
            label,
            value: data[index],
        }));

        dataArray.sort((a, b) => dayjs(a.label).valueOf() - dayjs(b.label).valueOf());

        return {
            data: dataArray.map((item) => item.value),
            labels: dataArray.map((item) => item.label),
        };
    };

    const sumDuplicates = ({ data, labels }) => {
        const labelSumMap = {};

        for (let i = 0; i < labels.length; i++) {
            const label = labels[i];
            const value = data[i];
            if (labelSumMap[label]) {
                labelSumMap[label] += value;
            } else {
                labelSumMap[label] = value;
            }
        }

        const uniqueLabels = Object.keys(labelSumMap);
        return {
            data: uniqueLabels.map((label) => labelSumMap[label]),
            labels: uniqueLabels,
        };
    };

    const cleanData = ({ data, labels }) => {
        const uniqueData = sumDuplicates({ data, labels });
        const sortedData = sortArrByDate({
            data: uniqueData.data,
            labels: uniqueData.labels,
        });
        return sortedData;
    };

    // LOGICA INVARIATA: useEffect PER ELABORARE I DATI
    useEffect(() => {

        const userRecord = {};
        const newUserRecord = {};
        const sessionRecord = {};
        const avgTimeRecord = {};
        const avgEngTimeRecord = {};
        const avgSessionPerUserRecord = {};

        let date = dayjs();
        let key = '';
        let data = null;
        let sortedData = {};

        let totalUsers = 0;
        let totalNewUsers = 0;
        let totalSession = 0;
        let totalAvgEngTime = 0;
        let totalAvgSessionTime = 0;
        let totalSessionPeruser = 0;

        aggregateList.forEach((item, index) => {
            // Converti la data in formato dayjs
            const dateF = dayjs(item.date);
            date = `${dateF.format('DD/MMM/YYYY')}`;

            // Somma i totali
            totalUsers += item.totalUniqueUsers || 0;
            totalSession += item.totalSessions || 0;
            totalNewUsers += item.newUsersCount || 0;

            // Verifica che avgSessionTime e avgEngagementTime esistano prima di chiamare getTimeInSecs
            const avgSessionSecs = item.avg_session_time ? getTimeInSecs(item.avg_session_time) : 0;
            const avgEngagementSecs = item.avg_engagement_time ? getTimeInSecs(item.avg_engagement_time) : 0;

            totalAvgSessionTime += avgSessionSecs * (item.totalSessions || 0);
            totalSessionPeruser += item.averageSessionsPerUser || 0;
            totalAvgEngTime += avgEngagementSecs * (item.totalUniqueUsers || 0); // Moltiplica per il numero di utenti

            key = `${date}`;
            userRecord[date] = userRecord[date] ? userRecord[date] + (item.totalUniqueUsers || 0) : (item.totalUniqueUsers || 0);
            newUserRecord[date] = newUserRecord[date] ? newUserRecord[date] + (item.newUsersCount || 0) : (item.newUsersCount || 0);
            sessionRecord[date] = sessionRecord[date] ? sessionRecord[date] + (item.totalSessions || 0) : (item.totalSessions || 0);

            if (!avgTimeRecord[date]) {
                avgTimeRecord[date] = { totalSeconds: 0, totalSessions: 0 };
            }
            avgTimeRecord[date].totalSeconds += avgSessionSecs * (item.totalSessions || 0);
            avgTimeRecord[date].totalSessions += item.totalSessions || 0;

            if (!avgEngTimeRecord[date]) {
                avgEngTimeRecord[date] = { totalSeconds: 0, totalUsers: 0 };
            }

            avgEngTimeRecord[date].totalSeconds += avgEngagementSecs * (item.totalUniqueUsers || 0);
            avgEngTimeRecord[date].totalUsers += (item.totalUniqueUsers || 0);

            avgSessionPerUserRecord[date] =
                (avgSessionPerUserRecord[date] || 0) +
                (item.totalUniqueUsers === 0 ? 0 : (item.totalSessions || 0) / (item.totalUniqueUsers || 1));
        });

        // Calcola i tempi medi finali dopo il loop
        Object.keys(avgTimeRecord).forEach((date) => {
            if (avgTimeRecord[date].totalSessions > 0) {
                const avgSecs = avgTimeRecord[date].totalSeconds / avgTimeRecord[date].totalSessions;
                // Memorizza il valore numerico per il grafico
                avgTimeRecord[date] = avgSecs;
            } else {
                avgTimeRecord[date] = 0;
            }
        });

        // Converte avgEngTimeRecord in minuti e limita a 2 decimali
        Object.keys(avgEngTimeRecord).forEach((date) => {
            if (avgEngTimeRecord[date].totalUsers > 0) {
                const avgSecs = avgEngTimeRecord[date].totalSeconds / avgEngTimeRecord[date].totalUsers;
                // Memorizza il valore numerico per il grafico
                avgEngTimeRecord[date] = avgSecs;
            } else {
                avgEngTimeRecord[date] = 0;
            }
        });

        // Imposta i dati utente
        data = getLinearGraphData('USERS', userRecord);

        // Somma duplicati e ordina
        sortedData = cleanData({
            data: data.datasets[0].data,
            labels: data.labels,
        });
        data.labels = sortedData.labels;
        data.datasets[0].data = sortedData.data;

        setUserData({ data, total: totalUsers });

        // Imposta i dati dei nuovi utenti
        data = getLinearGraphData('NEW USERS', newUserRecord);

        // Somma duplicati e ordina
        sortedData = cleanData({
            data: data.datasets[0].data,
            labels: data.labels,
        });
        data.labels = sortedData.labels;
        data.datasets[0].data = sortedData.data;

        setNewUserData({ data, total: totalNewUsers });

        // Imposta i dati delle sessioni
        data = getLinearGraphData('SESSIONS', sessionRecord);
        sortedData = sortArrByDate({
            data: data.datasets[0].data,
            labels: data.labels,
        });
        data.labels = sortedData.labels;
        data.datasets[0].data = sortedData.data;
        setSessionData({ data, total: totalSession });

        // Imposta i dati del tempo medio
        data = getLinearGraphData('AVG TIME', avgTimeRecord);
        sortedData = cleanData({
            data: data.datasets[0].data,
            labels: data.labels,
        });
        data.labels = sortedData.labels;
        data.datasets[0].data = sortedData.data;

        const avgTimeSecs = totalSession > 0 ? totalAvgSessionTime / totalSession : 0;
        const avgTimeFormatted = convertSecondsToMinutes(avgTimeSecs);

        setAvgTimeData({
            data,
            total: avgTimeFormatted || "0:00",
            rawValue: avgTimeSecs
        });

        // Imposta i dati del tempo di coinvolgimento medio
        data = getLinearGraphData('AVG ENG TIME', avgEngTimeRecord);
        sortedData = cleanData({
            data: data.datasets[0].data,
            labels: data.labels,
        });
        data.labels = sortedData.labels;
        data.datasets[0].data = sortedData.data;

        const avgEngTimeSecs = totalUsers > 0 ? totalAvgEngTime / totalUsers : 0;
        const avgEngTimeFormatted = convertSecondsToMinutes(avgEngTimeSecs);

        setAvgEngTimeData({
            data,
            total: avgEngTimeFormatted || "0:00",
            rawValue: avgEngTimeSecs
        });

        // Imposta i dati delle sessioni per utente
        data = getLinearGraphData('AVG S/U', avgSessionPerUserRecord);
        sortedData = cleanData({
            data: data.datasets[0].data,
            labels: data.labels,
        });
        data.labels = sortedData.labels;
        data.datasets[0].data = sortedData.data;

        setAvgSessionPerUserData({
            data,
            total: totalUsers > 0 ? (totalSession / totalUsers).toFixed(2) : "0.00",
        });
    }, [aggregateList]);

    // LISTA DEI DATI PER I GRAFICI
    const graphList = useMemo(() => [
        userData,
        sessionData,
        newUserData,
        avgTimeData,
        avgEngTimeData,
        avgSessionPerUserData,
    ], [userData, sessionData, newUserData, avgTimeData, avgEngTimeData, avgSessionPerUserData]);

    const tooltipTextMap = {
        userData: "Number of distinct users who visited the app",
        sessionData: "Total user sessions",
        newUserData: "Users who visited the app for the first time",
        avgTimeData: "Average time the app was in foreground per session",
        avgEngTimeData: "Average time the app was in foreground per user",
        avgSessionPerUserData: "Average number of sessions per user",
    };

    const dataItems = {
        userData,
        sessionData,
        newUserData,
        avgTimeData,
        avgEngTimeData,
        avgSessionPerUserData
    };

    const graphData = graphList[selectedDataIndex]?.data;

    useEffect(() => {
        const data = {
            user: userData?.data?.datasets[0].data,
            session: sessionData?.data?.datasets[0].data,
            newUser: newUserData?.data?.datasets[0].data,
            avgTime: avgTimeData?.data?.datasets[0].data,
            avgEngTime: avgEngTimeData?.data?.datasets[0].data,
            avgSpU: avgSessionPerUserData?.data?.datasets[0].data,
            dates: userData?.data?.labels,
        };

        for (let i = 0; i < overviewLabels.length; i++) {
            data[overviewLabels[i]] = graphList[i]?.total || 0;
        }
        getData(data);
    }, [graphData, graphList]);

    return (
        <>
            <SummaryGrid>
                {graphList.map((item, idx) => {
                    const tooltipKey = Object.keys(dataItems).find(
                        (key) => dataItems[key] === item
                    );
                    const tooltipText = tooltipTextMap[tooltipKey];

                    return (
                        <SummaryCard
                            key={`${item?.total || 0}_${idx}`}
                            loading={loading}
                            value={item?.total || 0}
                            active={idx === selectedDataIndex}
                            label={item.data?.datasets[0].label}
                            onClick={() => handleDataIndexChange(idx)}
                            toolTipText={tooltipText}
                            index={idx}
                            totalItems={graphList.length}
                        />
                    );
                })}
            </SummaryGrid>

            {loading ? (
                <LoadingCard />
            ) : (
                <SummaryGraphs
                    data={graphData || { datasets: [], labels: [] }}
                    isLoading={loading}
                    activeTab={graphList[selectedDataIndex]?.data?.datasets[0]?.label}
                    toolTipText={tooltipTextMap[Object.keys(dataItems).find(
                        (key) => dataItems[key] === graphList[selectedDataIndex]
                    )]}
                />
            )}
        </>
    );
};

export default AnalyticsOverviewCard;

export const getCardTitle = (
    aggregateList,
    role,
) => {

    let totalUniqueUsers = 0;
    let totalSessions = 0;
    let newUserCount = 0;
    let avgSessionTime = 0;
    let avgEngagementTime = 0;

    // Verifica che aggregateList sia definito e non vuoto
    if (!aggregateList || aggregateList.length === 0) {
        return { title: "N/A", value: 0 };
    }

    // Esamina ogni elemento dell'array aggregateList
    aggregateList.forEach((item, index) => {

        // Verifica se i campi necessari esistono in ciascun elemento
        if (item.uniqueUsers !== undefined) {
            totalUniqueUsers += item.uniqueUsers;
        }

        if (item.sessions !== undefined) {
            totalSessions += item.sessions;
        }

        if (item.newUserCount !== undefined) {
            newUserCount += item.newUserCount;
        }

        if (item.avgSessionTime !== undefined) {
            avgSessionTime += (item.avgSessionTime * item.sessions) || 0;
        }

        if (item.avgEngagementTime !== undefined) {
            avgEngagementTime += (item.avgEngagementTime * item.uniqueUsers) || 0;
        }
    });

    // Calcola le medie finali se necessario
    if (totalSessions > 0) {
        avgSessionTime = avgSessionTime / totalSessions;
    }

    if (totalUniqueUsers > 0) {
        avgEngagementTime = avgEngagementTime / totalUniqueUsers;
    }

    // Funzione per convertire secondi in formato mm:ss
    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return "0:00";
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    // Calcola il valore da mostrare in base al ruolo selezionato
    let value = 0;
    let title = "";

    switch (role) {
        case "Utenti":
            title = "Utenti Totali";
            value = totalUniqueUsers;
            break;
        case "Sessioni":
            title = "Sessioni Totali";
            value = totalSessions;
            break;
        case "Nuovi utenti":
            title = "Nuovi Utenti";
            value = newUserCount;
            break;
        case "Tempo medio":
            title = "Tempo Medio Sessione";
            value = formatTime(avgSessionTime);
            break;
        case "Tempo coinvolgimento":
            title = "Tempo Medio Coinvolgimento";
            value = formatTime(avgEngagementTime);
            break;
        case "Sessioni per utente":
            title = "Sessioni per Utente";
            value = totalUniqueUsers > 0 ? (totalSessions / totalUniqueUsers).toFixed(2) : 0;
            break;
        default:
            title = "N/A";
            value = 0;
    }

    return { title, value };
};